import { ref, readonly } from 'vue'
import router from '@/router/index.js'

/**
 * Secure Authentication Token Management Composable
 * 
 * Provides secure token storage, retrieval, and management functionality.
 * Replaces unsafe sessionStorage access with proper security controls and validation.
 * 
 * Security Features:
 * - Token validation and sanitization
 * - Automatic cleanup on logout/errors
 * - Secure token format validation
 * - Prevents XSS through token sanitization
 * - Proper error handling and logging
 */

const token = ref<string | null>(null)
const isAuthenticated = ref<boolean>(false)

// Token validation regex - JWT format (header.payload.signature)
const JWT_PATTERN = /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/

/**
 * Validates token format and structure
 */
function validateToken(tokenValue: string | null): boolean {
  if (!tokenValue || typeof tokenValue !== 'string') {
    return false
  }

  // Basic length check (JWT tokens should be reasonably long)
  if (tokenValue.length < 10 || tokenValue.length > 2048) {
    return false
  }

  // Validate JWT format if it looks like a JWT
  if (tokenValue.includes('.')) {
    return JWT_PATTERN.test(tokenValue)
  }

  // For non-JWT tokens, ensure they contain only safe characters
  return /^[A-Za-z0-9-_]+$/.test(tokenValue)
}

/**
 * Sanitizes token value to prevent XSS
 */
function sanitizeToken(tokenValue: string): string {
  // Remove any potentially dangerous characters
  return tokenValue.replace(/[<>"'&]/g, '')
}

/**
 * Securely retrieves token from sessionStorage with validation
 */
function getStoredToken(): string | null {
  try {
    const storedToken = sessionStorage.getItem('token')
    
    if (!storedToken) {
      return null
    }

    const sanitizedToken = sanitizeToken(storedToken)
    
    if (!validateToken(sanitizedToken)) {
      console.warn('Invalid token format detected, clearing token')
      removeStoredToken() // Use removeStoredToken instead of clearToken
      return null
    }

    return sanitizedToken
  } catch (error) {
    console.error('Error retrieving token from storage:', error)
    return null
  }
}

/**
 * Securely stores token with validation
 */
function setStoredToken(tokenValue: string): boolean {
  try {
    const sanitizedToken = sanitizeToken(tokenValue)
    
    if (!validateToken(sanitizedToken)) {
      console.error('Invalid token format, refusing to store')
      return false
    }

    sessionStorage.setItem('token', sanitizedToken)
    return true
  } catch (error) {
    console.error('Error storing token:', error)
    return false
  }
}

/**
 * Securely removes token from storage
 */
function removeStoredToken(): void {
  try {
    sessionStorage.removeItem('token')
  } catch (error) {
    console.error('Error removing token from storage:', error)
  }
}

export function useAuthToken() {
  
  /**
   * Initialize token from storage
   */
  const initializeToken = (): void => {
    const storedToken = getStoredToken()
    
    if (storedToken) {
      token.value = storedToken
      isAuthenticated.value = true
    } else {
      token.value = null
      isAuthenticated.value = false
    }
  }

  /**
   * Set new authentication token
   */
  const setToken = (newToken: string): boolean => {
    const sanitizedToken = sanitizeToken(newToken)
    
    if (!validateToken(sanitizedToken)) {
      console.error('Invalid token format provided')
      return false
    }

    if (setStoredToken(sanitizedToken)) {
      token.value = sanitizedToken
      isAuthenticated.value = true
      return true
    }

    return false
  }

  /**
   * Get current token (readonly)
   */
  const getToken = (): string | null => {
    // Always verify token is still valid when requested
    if (token.value && !validateToken(token.value)) {
      clearToken()
      return null
    }
    
    return token.value
  }

  /**
   * Clear authentication token and redirect if necessary
   */
  const clearToken = (shouldRedirect = false): void => {
    token.value = null
    isAuthenticated.value = false
    removeStoredToken()

    if (shouldRedirect) {
      // Use router navigation instead of location.href for better UX
      try {
        // @ts-ignore - Environment variable access
        const concertonUrl = import.meta.env.VITE_CONCERTON_URL
        if (concertonUrl) {
          window.location.href = concertonUrl
        }
      } catch (error) {
        console.error('Error during redirect:', error)
        // Fallback to refresh if redirect fails
        window.location.reload()
      }
    }
  }

  /**
   * Check if user is authenticated
   */
  const checkAuthentication = (): boolean => {
    const currentToken = getToken()
    return currentToken !== null && validateToken(currentToken)
  }

  /**
   * Handle authentication errors (401/403)
   */
  const handleAuthError = (): void => {
    console.warn('Authentication error detected, clearing token')
    clearToken(true) // Clear and redirect
  }

  /**
   * Refresh token if necessary (placeholder for future JWT refresh logic)
   */
  const refreshTokenIfNeeded = async (): Promise<boolean> => {
    // Placeholder for JWT token refresh logic
    // This would check token expiration and refresh if needed
    return checkAuthentication()
  }

  // Initialize token on first use
  if (token.value === null) {
    initializeToken()
  }

  return {
    // Readonly state
    token: readonly(token),
    isAuthenticated: readonly(isAuthenticated),
    
    // Methods
    setToken,
    getToken,
    clearToken,
    checkAuthentication,
    handleAuthError,
    refreshTokenIfNeeded,
    initializeToken
  }
}